import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Printer, RefreshCw, ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FIRM } from "@/lib/firm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Avery 5160 — 3 across x 10 down, 2.625in x 1in labels.
const LABELS_CSS = `
  .ml-wrap{background:#0b0f1a;min-height:100vh;padding:24px 12px;}
  .ml-sheet{background:#fff;width:8.5in;min-height:11in;margin:0 auto;box-shadow:0 10px 40px rgba(0,0,0,.45);font-family:Arial,Helvetica,sans-serif;color:#111;}
  .ml-grid{display:grid;grid-template-columns:repeat(3,2.625in);column-gap:0.125in;row-gap:0;padding:0.5in 0 0 0.1875in;box-sizing:border-box;}
  .ml-label{width:2.625in;height:1in;padding:0.1in 0.16in;box-sizing:border-box;overflow:hidden;display:flex;flex-direction:column;justify-content:center;}
  .ml-name{font-size:10pt;font-weight:700;line-height:1.15;}
  .ml-attn{font-size:9pt;font-weight:600;color:#111;line-height:1.2;}
  .ml-addr{font-size:9pt;line-height:1.2;white-space:pre-line;}
  .ml-outline .ml-label{outline:1px dashed #c7c7c7;outline-offset:-1px;}
  @media print{
    .ml-wrap{background:#fff!important;padding:0!important;}
    .ml-sheet{box-shadow:none!important;margin:0!important;width:auto!important;}
    .ml-outline .ml-label{outline:none!important;}
    .no-print{display:none!important;}
    .ml-grid{page-break-after:always;}
    @page{size:letter;margin:0;}
  }
`;

export default function MailingLabels() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [skipped, setSkipped] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [outline, setOutline] = useState(true);
  const [mode, setMode] = useState("recipients"); // 'recipients' | 'seutrade'

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const token = localStorage.getItem("th_admin_token");
      const r = await axios.get(`${API}/admin/counterparty-report`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const seen = new Set();
      const out = [];
      let skip = 0;
      (r.data.groups || []).forEach((g) => g.entries.forEach((e) => {
        const name = (e.name || "").trim();
        const addr = (e.address || "").trim();
        if (!name) return;
        if (!addr) { skip += 1; return; }
        const key = `${name.toLowerCase()}|${addr.toLowerCase()}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({ name, address: addr });
      }));
      out.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      setRows(out); setSkipped(skip);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load lender/servicer addresses.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // SEUTRADE return-address labels — full sheet using the Delaware address.
  const seutradeRows = Array.from({ length: 30 }, () => ({ name: FIRM.name, address: FIRM.address, seutrade: true }));
  const activeRows = mode === "seutrade" ? seutradeRows : (rows || []);

  // pad to full sheets of 30 so grid stays aligned
  const padded = activeRows ? [...activeRows] : [];
  while (activeRows && padded.length % 30 !== 0) padded.push(null);

  const tab = (id, label) => (
    <button
      data-testid={`ml-mode-${id}`}
      onClick={() => setMode(id)}
      style={{
        background: mode === id ? "#B8892B" : "transparent",
        color: mode === id ? "#fff" : "#cbd3e1",
        border: "1px solid #B8892B", borderRadius: 6, padding: "7px 12px",
        fontSize: 11, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", cursor: "pointer",
      }}
    >{label}</button>
  );

  return (
    <div className="ml-wrap" data-testid="mailing-labels-page">
      <style>{LABELS_CSS}</style>

      <div className="no-print" style={{ maxWidth: "8.5in", margin: "0 auto 10px", display: "flex", gap: 8, justifyContent: "center" }}>
        {tab("recipients", "Lender / Servicer Labels")}
        {tab("seutrade", "SEUTRADE Return (Delaware)")}
      </div>

      <div className="no-print" style={{ maxWidth: "8.5in", margin: "0 auto 14px", display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center" }}>
        <button data-testid="ml-back-btn" onClick={() => nav(-1)} style={btn("#1f2a44")}><ArrowLeft size={15} /> Back</button>
        <div style={{ color: "#cbd3e1", fontSize: 13 }}>
          {mode === "seutrade"
            ? "30 SEUTRADE return labels"
            : (rows ? `${rows.length} label${rows.length === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped (no address)` : ""}` : "")}
          {" · Avery 5160"}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <label style={{ color: "#cbd3e1", fontSize: 12, display: "flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
            <input type="checkbox" data-testid="ml-outline-toggle" checked={outline} onChange={(e) => setOutline(e.target.checked)} /> Guides
          </label>
          {mode === "recipients" && (
            <button data-testid="ml-refresh-btn" onClick={load} style={btn("#1f2a44")}><RefreshCw size={15} /> Refresh</button>
          )}
          <button data-testid="ml-print-btn" onClick={() => window.print()} disabled={mode === "recipients" && (loading || !rows || !rows.length)} style={btn("#B8892B")}><Printer size={15} /> Print Labels</button>
        </div>
      </div>

      {mode === "recipients" && loading && <p className="no-print" data-testid="ml-loading" style={{ color: "#cbd3e1", textAlign: "center", display: "flex", gap: 8, justifyContent: "center" }}><Loader2 className="animate-spin" size={16} /> Loading addresses…</p>}
      {mode === "recipients" && err && !loading && <p className="no-print" data-testid="ml-error" style={{ color: "#e8b4b4", textAlign: "center" }}>{err}</p>}
      {mode === "recipients" && !loading && !err && rows && rows.length === 0 && (
        <p className="no-print" data-testid="ml-empty" style={{ color: "#cbd3e1", textAlign: "center" }}>No lenders/servicers with a mailing address on file. Add addresses in the Fax rolodex or on a borrower deal.</p>
      )}

      {padded.length > 0 && (
        <div className={`ml-sheet ${outline ? "ml-outline" : ""}`}>
          <div className="ml-grid">
            {padded.map((e, i) => (
              <div className="ml-label" key={i} data-testid={e ? "ml-label" : "ml-label-empty"}>
                {e && (
                  <>
                    <div className="ml-name">{e.name}</div>
                    {!e.seutrade && <div className="ml-attn">Attn: Loss Mitigation Department</div>}
                    <div className="ml-addr">{e.address}</div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="no-print" style={{ maxWidth: "8.5in", margin: "14px auto 0", color: "#6b7590", fontSize: 11, textAlign: "center" }}>
        {mode === "seutrade"
          ? `${FIRM.name} return-address labels (Delaware). Prints on Avery 5160 (30 per sheet).`
          : `${FIRM.name} · Recipient labels addressed to each lender/servicer's Loss Mitigation Department. Prints on Avery 5160 (30 per sheet). Verify addresses before mailing.`}
      </p>
    </div>
  );
}

const btn = (bg) => ({
  display: "flex", alignItems: "center", gap: 7, background: bg, color: "#fff",
  border: "none", borderRadius: 6, padding: "9px 16px", fontWeight: 700,
  fontSize: "12px", letterSpacing: ".04em", textTransform: "uppercase", cursor: "pointer",
});
